import React, { useState, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  makeStyles,
  LinearProgress,
  Box,
  Typography,
  IconButton,
  Popover
} from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import CloudUploadIcon from '@material-ui/icons/CloudUpload';
import ArrowDropDownIcon from "@material-ui/icons/ArrowDropDown";
import { 
  UPLOAD_DATA_TAG_REPORT, 
  CLEAR_UPLOAD_STATE 
} from './DataTagConstants';
import Input from '../../components/shared/Input';
import { PrimaryButton } from '../../components/common/buttons';
import { createStaticRange, dateRangePickerStaticList } from '../../components/common/utilities';
import { formattedDate } from '../../components/views/GenerateReport/utilities';
import { UTCTooltip } from '../../components/views/GenerateReport/UTCTooltip';
import '../../components/views/GenerateReport/Report.scss';
import { DateRangePicker, Calendar, DefinedRange, createStaticRanges } from 'react-date-range';
import { startOfDay, endOfDay, addDays } from "date-fns";


const useStyles = makeStyles((theme) => ({
  dialog: {
    '& .MuiDialog-paper': {
      minWidth: '650px',
      maxWidth: '750px',
    }
  },
  dialogTitle: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    borderBottom: '1px solid #e0e0e0',
    padding: '16px 24px',
  },
  dialogContent: {
    overflowY: 'auto',
  },
  closeButton: {
    padding: '4px',
  },
  uploadArea: {
    border: '2px dashed #D0D0D0',
    borderRadius: '8px',
    textAlign: 'center',
    cursor: 'pointer',
    transition: 'all 0.3s ease',
    marginTop: '16px',
    '&:hover': {
      borderColor: '#081981',
      backgroundColor: '#f5f5f5',
    },
    '&.dragover': {
      borderColor: '#081981',
      backgroundColor: '#e8eaf6',
    }
  },
  uploadIcon: {
    fontSize: '48px',
    color: '#081981',
  },
  fileInput: {
    display: 'none',
  },
  fileName: {
    marginTop: '16px',
    padding: '12px',
    backgroundColor: '#f5f5f5',
    borderRadius: '4px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  progressContainer: {
    marginTop: '16px',
  },
  textField: {
    marginLeft: '8px',
    marginRight: '8px',
    width: 100,
  },
  textFieldStartEnd: {
    marginLeft: '8px',
    marginRight: '8px',
    width: 220,
  },
  popover: {
    padding: '16px',
  },
  dateContainer: {
    display: 'flex',
    gap: '16px',
    marginTop: '16px',
  },
  dateField: {
    flex: 1,
  },
}));


const dateRangePickerStaticListExpiry = () => {
  const customDateObjects = [
    {
      label: "10 days",
      range: () => ({
        startDate: addDays(new Date(), 10),
        endDate: null,
      }),
    },
    {
      label: "20 days",
      range: () => ({
        startDate: addDays(new Date(), 20),
        endDate: null,
      }),
    },
    {
      label: "30 days",
      range: () => ({
        startDate: addDays(new Date(), 30),
        endDate: null,
      }),
    },
    {
      label: "6 Months",
      range: () => ({
        startDate: addDays(new Date(), 180),
        endDate: null,
      }),
    },
    {
      label: "1 Year",
      range: () => ({
        startDate: addDays(new Date(), 365),
        endDate: null,
      }),
    },
    {
      label: "2 Years",
      range: () => ({
        startDate: addDays(new Date(), 730),
        endDate: null,
      }),
    },
  ];
  return customDateObjects;
};

function UploadReportModal({ open, onClose, reportType="dataTagReport" }) {
  const classes = useStyles();
  const dispatch = useDispatch();
  const fileInputRef = useRef(null);
  
  const [reportName, setReportName] = useState('');
  const [description, setDescription] = useState('');
  const [section, setSection] = useState('');
  const [file, setFile] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const [disabled, setDisabled] = useState(true);
  
  const currentDate = new Date();
  const [reportPeriodStart, setReportPeriodStart] = useState(currentDate);
  const [reportPeriodEnd, setReportPeriodEnd] = useState(endOfDay(currentDate));
  const [expiryDate, setExpiryDate] = useState(endOfDay(new Date(currentDate.getFullYear() + 1, currentDate.getMonth(), currentDate.getDate())));
  
  const [periodAnchorEl, setPeriodAnchorEl] = useState(null);
  const [expiryAnchorEl, setExpiryAnchorEl] = useState(null);

  const staticRangesExpiry = [...createStaticRanges(dateRangePickerStaticListExpiry())];

  const [expiryState, setExpiryState] = useState([
    {
      startDate: endOfDay(new Date(currentDate.getFullYear() + 1, currentDate.getMonth(), currentDate.getDate())),
      endDate: null,
      key: "selection",
    },
  ]);

  
  const handleExpiryDateSelect = (date, isStaticSelection) => {
    let selectedDate = date.startDate ? date.startDate : date;
    setExpiryDate(endOfDay(selectedDate));
    setExpiryState([
      {
        startDate: endOfDay(selectedDate),
        endDate: null,
        key: "selection",
      },
    ]);
    if (isStaticSelection) {
      setExpiryAnchorEl(null);
    }
  };

  const uploadLoader = useSelector(
    (state) => state.DataTagReportsReducer.uploadLoader
  );
  
  const uploadProgress = useSelector(
    (state) => state.DataTagReportsReducer.uploadProgress
  );

  const uploadSnackBarMessage = useSelector(
    (state) => state.DataTagReportsReducer.uploadSnackBarMessage
  );

  const staticRanges = [...createStaticRange(dateRangePickerStaticList())];

  // Listen to custom upload progress events
  useEffect(() => {
    const handleProgress = (event) => {
      dispatch({ type: 'SET_UPLOAD_PROGRESS', payload: event.detail });
    };
    
    window.addEventListener('uploadProgress', handleProgress);
    
    return () => {
      window.removeEventListener('uploadProgress', handleProgress);
    };
  }, [dispatch]);

  // Validate form fields
  useEffect(() => {
    if (reportName.trim() && file) {
      setDisabled(false);
    } else {
      setDisabled(true);
    }
  }, [reportName, file]);

  const handleClose = () => {
    setReportName('');
    setDescription('');
    setSection('');
    setFile(null);
    setReportPeriodStart(currentDate);
    setReportPeriodEnd(endOfDay(currentDate));
    setExpiryDate(endOfDay(new Date(currentDate.getFullYear() + 1, currentDate.getMonth(), currentDate.getDate())));
    dispatch({ type: CLEAR_UPLOAD_STATE });
    onClose();
  };

  // Close modal on successful upload
  useEffect(() => {
    if (uploadSnackBarMessage?.status === 'success') {
      setTimeout(() => {
        handleClose();
      }, 1500);
    }
  }, [uploadSnackBarMessage]);

  const handleDragOver = (e) => {
    e.preventDefault();
    setDragOver(true);
  };

  const handleDragLeave = () => {
    setDragOver(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    
    const droppedFile = e.dataTransfer.files[0];
    if (droppedFile) {
      setFile(droppedFile);
    }
  };

  const handleFileSelect = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      setFile(selectedFile);
    }
  };

  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  const handleRemoveFile = () => {
    setFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleUpload = () => {
    if (!disabled && file) {
      
      dispatch({
        type: UPLOAD_DATA_TAG_REPORT,
        payload: {
          file,
          reportType,
          reportName,
          description,
          section,
          reportPeriodStart: startOfDay(reportPeriodStart).toISOString(),
          reportPeriodEnd: endOfDay(reportPeriodEnd).toISOString(),
          expiryDate: endOfDay(expiryDate).toISOString()
        }
      });
    }
  };

  const openPeriodPopover = Boolean(periodAnchorEl);
  const openExpiryPopover = Boolean(expiryAnchorEl);

  const handlePeriodFieldClick = (event) => {
    setPeriodAnchorEl(event.currentTarget);
  };

  const handleExpiryFieldClick = (event) => {
    setExpiryAnchorEl(event.currentTarget);
  };

  const handlePopoverClose = () => {
    setPeriodAnchorEl(null);
    setExpiryAnchorEl(null);
  };

  const renderDateInputWithPopover = (
    label,
    startValue,
    endValue,
    handleClick,
    popoverId,
    openPopover,
    anchorEl,
    handleClose,
    onDateChange,
    isSingleDate = false
  ) => {
    return (
      <div className="generate_report_dates date-picker-flex">
        <div className="generate_report_date_header">{label}</div>
        <div className="generate_report_date_selector">
          <TextField
            value={isSingleDate ? formattedDate(new Date(startValue)) : `${formattedDate(new Date(startValue))} - ${formattedDate(new Date(endValue))}`}
            className={isSingleDate ? classes.textField : classes.textFieldStartEnd}
            InputProps={{
              readOnly: true,
            }}
            onClick={handleClick}
            fullWidth
          />
          <ArrowDropDownIcon
            onClick={handleClick}
            className={`arrow_drop_down ${openPopover ? "icon_clicked" : ""}`}
          />
          <Popover
            id={popoverId}
            open={openPopover}
            anchorEl={anchorEl}
            onClose={handleClose}
            anchorOrigin={{
              vertical: "bottom",
              horizontal: "center",
            }}
            transformOrigin={{
              vertical: "top",
              horizontal: "center",
            }}
          >
            <div className={classes.popover}>
              <DateRangePicker
                ranges={[
                  {
                    startDate: new Date(startValue),
                    endDate: new Date(endValue),
                    key: "selection",
                  },
                ]}
                onChange={(ranges) => {
                  onDateChange(ranges.selection.startDate, endOfDay(ranges.selection.endDate));
                }}
                inputRanges={[]}
                staticRanges={staticRanges}
                maxDate={isSingleDate ? undefined : startOfDay(new Date())}
              />
            </div>
          </Popover>
        </div>
      </div>
    );
  };

  return (
    <Dialog
      open={open}
      onClose={uploadLoader ? undefined : handleClose}
      className={classes.dialog}
      maxWidth="md"
    >
      <DialogTitle className={classes.dialogTitle} disableTypography>
        <Typography variant="h6" className="upload-modal-title">
          Upload Report
        </Typography>
        <IconButton
          className={classes.closeButton}
          onClick={handleClose}
          disabled={uploadLoader}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      
      <DialogContent className={classes.dialogContent}>
        {/* Two Column Layout: Name/Section on left, Description on right */}
        <div className="upload-two-column-layout">
          {/* Left Column - Report Name and Section */}
          <div className="upload-left-column">
            {/* Report Name */}
            <div className="generate_report_content">
              <div className="generate_report_heading">
                <span>Report Name</span>
                <span className="shareReportErrorMessage">*</span>
              </div>
              <div className="generate_report_input">
                <Input
                  type="text"
                  value={reportName}
                  classname="number_input"
                  placeholder="Enter Report Name"
                  eventHandler={(value) => setReportName(value)}
                  disabled={uploadLoader}
                />
              </div>
            </div>

            {/* Section */}
            <div className="generate_report_content">
              <div className="generate_report_heading">
                <span>Sections</span>
              </div>
              <div className="generate_report_input">
                <Input
                  type="text"
                  value={section}
                  classname="number_input"
                  placeholder="Enter section (optional)"
                  eventHandler={(value) => setSection(value)}
                  disabled={uploadLoader}
                />
              </div>
            </div>
          </div>

          {/* Right Column - Description */}
          <div className="upload-right-column">
            <div className="generate_report_content">
              <div className="generate_report_heading">
                <span>Description</span>
              </div>
              <div className="generate_report_input description-input">
                <TextField
                  multiline
                  minRows={3}
                  maxRows={3}
                  value={description}
                  placeholder="Enter description (optional)"
                  onChange={(e) => setDescription(e.target.value)}
                  disabled={uploadLoader}
                  variant="outlined"
                  fullWidth
                />
              </div>
            </div>
          </div>
        </div>

        {/* Report Period */}
        <div className="generate_report_date_container upload-section-spacing">
          <div className="generate_report_heading header-icon">
            Report Period {UTCTooltip()}
          </div>
          <div className={classes.dateContainer}>
            {renderDateInputWithPopover(
              "Period Range",
              reportPeriodStart,
              reportPeriodEnd,
              handlePeriodFieldClick,
              openPeriodPopover ? "period-popover" : undefined,
              openPeriodPopover,
              periodAnchorEl,
              handlePopoverClose,
              (start, end) => {
                setReportPeriodStart(start);
                setReportPeriodEnd(end);
              }
            )}
          </div>
        </div>

        {/* Expiry Date */}
        <div className="generate_report_date_container upload-section-spacing">
          <div className="generate_report_heading header-icon">
            Expiry Date {UTCTooltip()}
          </div>
          <div className={classes.dateContainer}>
            <div className="generate_report_dates date-picker-flex">
              <div className="generate_report_date_header">Expiry Date</div>
              <div className="generate_report_date_selector">
                <TextField
                  value={formattedDate(new Date(expiryDate))}
                  className={classes.textField}
                  InputProps={{
                    readOnly: true,
                  }}
                  onClick={handleExpiryFieldClick}
                  fullWidth
                />
                <ArrowDropDownIcon
                  onClick={handleExpiryFieldClick}
                  className={`arrow_drop_down ${openExpiryPopover ? "icon_clicked" : ""}`}
                />
                <Popover
                  id={openExpiryPopover ? "expiry-popover" : undefined}
                  open={openExpiryPopover}
                  anchorEl={expiryAnchorEl}
                  onClose={handlePopoverClose}
                  anchorOrigin={{
                    vertical: "bottom",
                    horizontal: "center",
                  }}
                  transformOrigin={{
                    vertical: "top",
                    horizontal: "center",
                  }}
                >
                  <div className="dateRange-time-picker" style={{ height: "400px", zIndex: "1" }}>
                    <div className="dateRanger-header">
                      <span className="quick-range">Expire after</span>
                      <span className="select-range" style={{ marginLeft: "10rem" }}>
                        Select Expiry Date
                      </span>
                    </div>
                    <div style={{ display: "flex" }}>
                      <DefinedRange
                        onChange={(item) => {
                          setExpiryState([item.selection]);
                          handleExpiryDateSelect(item.selection, true);
                        }}
                        ranges={expiryState}
                        staticRanges={staticRangesExpiry}
                        inputRanges={[]}
                      />
                      <div className="calenderContainer">
                        <Calendar
                          onChange={(date) => handleExpiryDateSelect(date, false)}
                          minDate={new Date()}
                          date={new Date(expiryDate)}
                          className="customDate"
                        />
                      </div>
                    </div>
                  </div>
                </Popover>
              </div>
            </div>
          </div>
        </div>

        {/* File Upload Area */}
        <div className="upload-section-spacing">
          <div className="generate_report_heading">
            <span>Upload File</span>
            <span className="shareReportErrorMessage">*</span>
          </div>
          <div
            className={`${classes.uploadArea} ${dragOver ? 'dragover' : ''}`}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            onClick={handleUploadClick}
          >
            <CloudUploadIcon className={classes.uploadIcon} />
            <Typography variant="body1" className="upload-drag-text-primary">
              Drag & Drop your file here
            </Typography>
            <Typography variant="body2" className="upload-drag-text-secondary">
              or click to browse
            </Typography>
            <input
              ref={fileInputRef}
              type="file"
              className={classes.fileInput}
              onChange={handleFileSelect}
              disabled={uploadLoader}
            />
          </div>
        </div>

        {/* Selected File Display */}
        {file && (
          <div className={classes.fileName}>
            <Typography variant="body2" className="upload-file-name-text">
              {file.name} ({(file.size / (1024 * 1024)).toFixed(2)} MB)
            </Typography>
            <IconButton
              size="small"
              onClick={handleRemoveFile}
              disabled={uploadLoader}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </div>
        )}

        {/* Progress Bar */}
        {uploadLoader && (
          <div className={classes.progressContainer}>
            <Box display="flex" alignItems="center">
              <Box width="100%" mr={1}>
                <LinearProgress variant="determinate" value={uploadProgress} />
              </Box>
              <Box minWidth={35}>
                <Typography variant="body2" color="textSecondary">
                  {`${uploadProgress}%`}
                </Typography>
              </Box>
            </Box>
            <Typography variant="body2" className="upload-progress-text">
              Uploading file...
            </Typography>
          </div>
        )}
      </DialogContent>

      <DialogActions className="upload-dialog-actions">
        <Button
          onClick={handleClose}
          disabled={uploadLoader}
          className="upload-cancel-button"
        >
          Cancel
        </Button>
        <PrimaryButton
          onClick={handleUpload}
          disabled={disabled || uploadLoader}
        >
          Upload Report
        </PrimaryButton>
      </DialogActions>
    </Dialog>
  );
}

export default UploadReportModal;
